export type PlanTier = 'free' | 'trial' | 'basic' | 'premium';

export type Feature =
  | 'export'
  | 'voice'
  | 'ai'
  | 'analytics'
  | 'sharing'
  | 'branding'
  | 'patients';

// ── Credits system ──────────────────────────────────────────
export const CREDITS_PER_PATIENT = 1000;
export const CREDITS_PER_STEP = 28;   // ~36 steps × 28 ≈ 1000 per patient
export const STARTER_CREDITS = 4000;  // 4 full patients for first 500 users

export const PLAN_LABELS: Record<PlanTier, string> = {
  free: 'Basic',
  trial: 'Starter',
  basic: 'Plus',
  premium: 'Pro',
};

export const PLAN_COLORS: Record<PlanTier, string> = {
  free: 'gray',
  trial: 'amber',
  basic: 'blue',
  premium: 'purple',
};

export const AI_LIMIT: Record<PlanTier, number> = {
  free: 0,     // Basic: no AI calls
  trial: -1,   // unlimited while credits remain = -1
  basic: 10,
  premium: -1,
};

export const PATIENT_LIMIT: Record<PlanTier, number> = {
  free: 10,    // Basic: 10 patients per month
  trial: -1,
  basic: -1,
  premium: -1,
};

/** Central feature access gate */
export function canAccess(feature: Feature, plan: PlanTier): boolean {
  switch (feature) {
    case 'export':
      return plan === 'premium' || plan === 'basic';   // Plus + Pro
    case 'voice':
      return plan === 'basic' || plan === 'premium';   // Plus + Pro (not Basic free)
    case 'ai':
      return plan !== 'free'; // not available on Basic free plan
    case 'analytics':
      return plan !== 'free';
    case 'sharing':
      return plan !== 'free';
    case 'branding':
      return false; // removed from all plans
    case 'patients':
      return true; // soft limit via PATIENT_LIMIT
    default:
      return false;
  }
}

/** Human-readable reason for blocking */
export const FEATURE_UPGRADE_REASON: Partial<Record<Feature, string>> = {
  export: 'Download and share professional reports with your patients.',
  voice: 'Save time with voice-powered charting.',
  ai: 'Continue with unlimited AI assistance.',
  analytics: 'Unlock advanced analytics for your practice.',
  sharing: 'Share reports via WhatsApp or email.',
  branding: 'Add your clinic branding to all reports.',
  patients: 'Store unlimited patient records.',
};

export const FEATURE_REQUIRED_PLAN: Partial<Record<Feature, PlanTier[]>> = {
  export: ['basic', 'premium'],
  voice: ['basic', 'premium'],
  ai: ['basic', 'premium'],
  analytics: ['basic', 'premium'],
  sharing: ['basic', 'premium'],
  patients: ['basic', 'premium'],
};
